import constantURL from "./ConstantURL";

const ROLE_FAMILY_MEMBER = "FAMILYMEMBER";
const ROLE_CAREGIVER = "CAREGIVER";
const ROLE_ADMIN = "ADMIN";

export const getFamilyMemberFullName = (familyMemberInfo = {}) => {
  const directName = String(familyMemberInfo.fullName || familyMemberInfo.familyMemberName || "").trim();
  if (directName) {
    return directName;
  }

  return [familyMemberInfo.familyMemberFirstName || familyMemberInfo.firstName, familyMemberInfo.familyMemberLastName || familyMemberInfo.lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
};

/**
 * Reports the admin (JSP) session held in the JSESSIONID cookie. Used on the
 * React login screen to bounce an already-signed-in admin into the JSP app.
 * @returns {Promise<{authenticated: boolean, role?: string, communitySelected?: boolean, redirectUrl?: string}>}
 */
export const checkAdminSession = async () => {
  try {
    const response = await fetch(constantURL.authSession, {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok || data?.status !== "success" || !data?.data?.authenticated) {
      return { authenticated: false };
    }

    const payload = data.data;
    return {
      authenticated: true,
      role: String(payload.role || "").toLowerCase(),
      communitySelected: Boolean(payload.communitySelected),
      redirectUrl: payload.redirectUrl || "",
    };
  } catch (error) {
    console.error("[checkAdminSession] request failed", error);
    return { authenticated: false };
  }
};

/**
 * Step 1 of sign-in: confirm the email belongs to an active account and learn
 * which role(s) it holds — CAREGIVER, FAMILYMEMBER and/or ADMIN. Email only:
 * mobile-number identifiers are not offered on this screen even though
 * validateUserCredential.do accepts them.
 * @param {{emailId: string}} params
 * @returns {Promise<{success: boolean, roles: Array<{role: string, firstLogin: boolean}>, message: string}>}
 */
export const validateCredential = async ({ emailId }) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();

  if (!normalizedEmail) {
    return { success: false, roles: [], message: "Email is required." };
  }

  try {
    const response = await fetch(constantURL.authValidateUserCredential, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ emailId: normalizedEmail }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok || data?.status !== "success" || !data?.data) {
      return {
        success: false,
        roles: [],
        message: data?.message || "Unable to verify this email. Please try again.",
      };
    }

    const roles = Array.isArray(data.data.roles)
      ? data.data.roles
          .filter((entry) => entry && entry.role)
          .map((entry) => ({
            role: String(entry.role).toUpperCase(),
            firstLogin: Boolean(entry.firstLogin),
          }))
      : [];

    if (!roles.length) {
      return { success: false, roles: [], message: data.message || "User does not exist." };
    }

    return { success: true, roles, message: data.message || "" };
  } catch (error) {
    console.error("[validateCredential] request failed", error);
    return {
      success: false,
      roles: [],
      message: "Unable to verify this email. Please try again.",
    };
  }
};

/**
 * Single sign-in entry for admin / caregiver / family. The identifier may be a
 * username (admin) or an email (caregiver / family). Admin sessions are set
 * server-side (cookies); caregiver/family return a token to persist.
 *
 * `role` is the one the user picked from validateUserCredential.do. The server
 * needs it to disambiguate accounts registered for more than one role, and it
 * resolves the returned payload against that role.
 */
export const commonLogin = async ({ identifier, password, role }) => {
  const normalizedIdentifier = String(identifier || "").trim();
  const normalizedPassword = String(password ?? "");
  const normalizedRequestedRole = String(role || "")
    .trim()
    .toUpperCase();

  if (!normalizedIdentifier || !normalizedPassword) {
    return { success: false, message: "Username/email and password are required." };
  }

  try {
    const response = await fetch(constantURL.authCommonLogin, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        identifier: normalizedIdentifier,
        password: normalizedPassword,
        ...(normalizedRequestedRole && { role: normalizedRequestedRole }),
      }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok || data?.status !== "success" || !data?.data) {
      return {
        success: false,
        message: data?.message || "Invalid credentials.",
      };
    }

    const payload = data.data;
    const normalizedRole = String(payload.role || "").toUpperCase();

    // Admin: the endpoint already established the JSP session (cookies set on
    // this same-origin response). No token/localStorage — just navigate to the
    // admin app, which sees the active session and renders its menu.
    if (normalizedRole === ROLE_ADMIN) {
      return {
        success: true,
        role: "admin",
        redirectUrl: payload.redirectUrl || constantURL.adminEntry,
        message: data.message || "",
      };
    }

    if (!payload.token) {
      return {
        success: false,
        message: data?.message || "Invalid credentials.",
      };
    }

    const { token, familyMemberInfo, caregiverInfo } = payload;

    if (normalizedRole === ROLE_FAMILY_MEMBER) {
      const normalizedFamilyMemberInfo = {
        ...(familyMemberInfo || {}),
        familyMemberName: getFamilyMemberFullName(familyMemberInfo),
      };

      return {
        success: true,
        role: "family",
        token,
        familyMemberInfo: normalizedFamilyMemberInfo,
        message: data.message || "",
      };
    }

    if (normalizedRole === ROLE_CAREGIVER) {
      return {
        success: true,
        role: "caregiver",
        token,
        caregiverInfo: caregiverInfo || {},
        message: data.message || "",
      };
    }

    return {
      success: false,
      message: data?.message || "Unsupported user role.",
    };
  } catch {
    return {
      success: false,
      message: "Unable to sign in. Please try again.",
    };
  }
};

/**
 * commonLoginV2.do: single-shot sign-in that needs only {identifier, password}
 * — no role param. The server checks the admin table first, then family
 * member, then caregiver, and returns whichever matches.
 */
export const commonLoginV2 = async ({ identifier, password }) => {
  const normalizedIdentifier = String(identifier || "").trim();
  const normalizedPassword = String(password ?? "");

  if (!normalizedIdentifier || !normalizedPassword) {
    return { success: false, message: "Username/email and password are required." };
  }

  try {
    const response = await fetch(constantURL.authCommonLoginV2, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        identifier: normalizedIdentifier,
        password: normalizedPassword,
      }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok || data?.status !== "success" || !data?.data) {
      return {
        success: false,
        message: data?.message || "Invalid credentials.",
      };
    }

    const payload = data.data;
    const normalizedRole = String(payload.role || "").toUpperCase();

    // Admin: the endpoint already established the JSP session (cookies set on
    // this same-origin response). No token/localStorage — just navigate to the
    // admin app, which sees the active session and renders its menu.
    if (normalizedRole === ROLE_ADMIN) {
      return {
        success: true,
        role: "admin",
        redirectUrl: payload.redirectUrl || constantURL.adminEntry,
        message: data.message || "",
      };
    }

    if (!payload.token) {
      return {
        success: false,
        message: data?.message || "Invalid credentials.",
      };
    }

    if (normalizedRole === ROLE_FAMILY_MEMBER) {
      const { token, familyMemberInfo } = payload;
      const normalizedFamilyMemberInfo = {
        ...(familyMemberInfo || {}),
        familyMemberName: getFamilyMemberFullName(familyMemberInfo),
      };

      return {
        success: true,
        role: "family",
        token,
        familyMemberInfo: normalizedFamilyMemberInfo,
        message: data.message || "",
      };
    }

    if (normalizedRole === ROLE_CAREGIVER) {
      const { token, caregiverInfo } = payload;

      return {
        success: true,
        role: "caregiver",
        token,
        caregiverInfo: caregiverInfo || {},
        message: data.message || "",
      };
    }

    return {
      success: false,
      message: data?.message || "Unsupported user role.",
    };
  } catch {
    return {
      success: false,
      message: "Unable to sign in. Please try again.",
    };
  }
};

/**
 * First-login setup for Family Member accounts: validateUserCredential.do
 * flags these with `firstLogin: true` because no password exists yet. After
 * OTP verification, this creates the password and — per the API contract —
 * logs the user in immediately (the success response carries a token), so
 * the result is normalized into the same {role, token, familyMemberInfo}
 * shape commonLogin returns, letting the caller reuse resolvePostLoginNavigation.
 */
export const setupFirstLoginPassword = async ({ emailId, role, password }) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  const normalizedRole = String(role || "")
    .trim()
    .toUpperCase();
  const normalizedPassword = String(password ?? "");

  if (!normalizedEmail || !normalizedRole || !normalizedPassword) {
    return { success: false, message: "Email, role and password are required." };
  }

  try {
    const response = await fetch(constantURL.authSetUserPassword, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        emailId: normalizedEmail,
        role: normalizedRole,
        password: normalizedPassword,
      }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok || data?.status !== "success" || !data?.data?.token) {
      return {
        success: false,
        message: data?.message || "Unable to set up your password. Please try again.",
      };
    }

    const payload = data.data;
    const normalizedResponseRole = String(payload.role || normalizedRole).toUpperCase();

    if (normalizedResponseRole === ROLE_FAMILY_MEMBER) {
      const normalizedFamilyMemberInfo = {
        ...(payload.familyMemberInfo || {}),
        familyMemberName: getFamilyMemberFullName(payload.familyMemberInfo),
      };

      return {
        success: true,
        role: "family",
        token: payload.token,
        familyMemberInfo: normalizedFamilyMemberInfo,
        message: data.message || "",
      };
    }

    if (normalizedResponseRole === ROLE_CAREGIVER) {
      return {
        success: true,
        role: "caregiver",
        token: payload.token,
        caregiverInfo: payload.caregiverInfo || {},
        message: data.message || "",
      };
    }

    return {
      success: false,
      message: data?.message || "Unsupported user role.",
    };
  } catch {
    return {
      success: false,
      message: "Unable to set up your password. Please try again.",
    };
  }
};

export const loginWithEmailPassword = async ({ email, password }) => {
  const normalizedEmail = String(email || "").trim();
  const normalizedPassword = String(password ?? "");

  if (!normalizedEmail || !normalizedPassword) {
    return { success: false, message: "Email and password are required." };
  }

  try {
    const response = await fetch(constantURL.authLogin, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: normalizedEmail,
        password: normalizedPassword,
      }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok || data?.status !== "success" || !data?.data?.token) {
      return {
        success: false,
        message: data?.message || "Invalid email or password.",
      };
    }

    const { token, role, familyMemberInfo, caregiverInfo } = data.data;
    const normalizedRole = String(role || "").toUpperCase();

    if (normalizedRole === ROLE_FAMILY_MEMBER) {
      const normalizedFamilyMemberInfo = {
        ...(familyMemberInfo || {}),
        familyMemberName: getFamilyMemberFullName(familyMemberInfo),
      };

      return {
        success: true,
        role: "family",
        token,
        familyMemberInfo: normalizedFamilyMemberInfo,
        message: data.message || "",
      };
    }

    if (normalizedRole === ROLE_CAREGIVER) {
      return {
        success: true,
        role: "caregiver",
        token,
        caregiverInfo: caregiverInfo || {},
        message: data.message || "",
      };
    }

    return {
      success: false,
      message: data?.message || "Unsupported user role.",
    };
  } catch {
    return {
      success: false,
      message: "Unable to sign in. Please try again.",
    };
  }
};
